import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";
import { AlertIcon, CloseIcon } from "@/components/icons";
import { useToasts, type Toast, type ToastTone } from "./toastStore";

/**
 * The stack of failure notices (backlog item 11).
 *
 * Two live regions, both **always in the DOM**, because a live region added at
 * the same moment as its first message is not announced at all — the assistive
 * technology has nothing to observe a change against. That is the part
 * hand-rolled toasts get wrong, and it is invisible in every test that does
 * not use a screen reader.
 *
 * Mounted by `AppShell`, so the print routes never see it (PRD FR-9.1); the
 * `print:hidden` covers a reader pressing Cmd-P on an ordinary page.
 */

/** Long enough to read a sentence written by the server, short enough to leave. */
const DISMISS_MS = 9_000;

const TONES: Record<ToastTone, string> = {
  error: "border-error/40 bg-error-subtle",
  warning: "border-warning/40 bg-warning-subtle",
  info: "border-border bg-surface",
};

const MARKS: Record<ToastTone, string> = {
  error: "text-error",
  warning: "text-warning",
  info: "text-ink-secondary",
};

export function Toaster() {
  const toasts = useToasts((state) => state.toasts);
  const dismiss = useToasts((state) => state.dismiss);
  // A stack that expires while it is being read is a stack that was never
  // shown. Hover or keyboard focus anywhere in it holds every timer.
  const [held, setHeld] = useState(false);

  const polite = toasts.filter((toast) => !toast.urgent);
  const urgent = toasts.filter((toast) => toast.urgent);

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4",
        "sm:items-end sm:p-6 print:hidden",
      )}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      {/* Real boxes rather than `display: contents`: an element carrying a role
          and dropped from the layout tree has been dropped from the
          accessibility tree too often for a live region to risk it. */}
      <div
        role="alert"
        aria-live="assertive"
        className="flex w-full flex-col items-center gap-2 sm:items-end"
      >
        {urgent.map((toast) => (
          <ToastRow key={toast.id} toast={toast} held={held} onDismiss={dismiss} />
        ))}
      </div>
      <div
        role="status"
        aria-live="polite"
        className="flex w-full flex-col items-center gap-2 sm:items-end"
      >
        {polite.map((toast) => (
          <ToastRow key={toast.id} toast={toast} held={held} onDismiss={dismiss} />
        ))}
      </div>
    </div>
  );
}

function ToastRow({
  toast,
  held,
  onDismiss,
}: {
  toast: Toast;
  held: boolean;
  onDismiss: (id: number) => void;
}) {
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;

  useEffect(() => {
    if (held) return;
    const timer = window.setTimeout(() => dismiss.current(toast.id), DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, held]);

  return (
    <div
      data-error-code={toast.code}
      className={cn(
        "pointer-events-auto w-full max-w-sm rounded-container border shadow-popover",
        "flex items-start gap-2.5 px-3.5 py-3",
        TONES[toast.tone],
      )}
    >
      <span className={cn("mt-0.5 shrink-0", MARKS[toast.tone])}>
        <AlertIcon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="type-support text-ink font-medium">
          {toast.title}
          {toast.count > 1 ? (
            <span className="text-ink-secondary font-normal"> · {toast.count} times</span>
          ) : null}
        </p>
        {/* The server's sentence, verbatim — in the support role, not the
            caption one, which is uppercased by the theme. A refusal read back
            as a shout is a different sentence. `break-words` because one can
            carry a table or column name with no spaces in it. */}
        <p className="type-support text-ink-secondary mt-0.5 break-words">{toast.message}</p>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(toast.id)}
        className="text-ink-disabled hover:text-ink motion-fast rounded-inner -me-1 -mt-1 p-1 transition-colors"
      >
        <CloseIcon className="size-3.5" />
      </button>
    </div>
  );
}
